var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var PeriodDiffConfig_1;
/* eslint-disable import/no-duplicates */
import '@symon-ai/wisepipe-base-pipe'; // force import for JsonSchemaV2
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { Column } from '@symon-ai/wisepipe-common-data';
import { JSON, JsonDiscriminatorValue, JsonSchemaV2 } from '@symon-ai/wisepipe-serialize';
import { PeriodOperandColumn } from './periodOperandColumn';
import './periodOperand'; // force import for JsonSchemaV2
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationResult } from './validation/toolValidationResult';
import { validateSourceWithDetail } from './validation/validateHelper';
import { periodDiffConfigSchemaValidation } from '../validations.generated';
export const periodDiffConfigSchema = {
    $id: 'urn:wisepipe-common-pipe:PeriodDiffConfig',
    type: 'object',
    properties: {
        lhs: { $ref: 'urn:wisepipe-common-pipe:PeriodOperand' },
        rhs: { $ref: 'urn:wisepipe-common-pipe:PeriodOperand' },
        includeStartPeriod: {
            oneOf: [
                {
                    $ref: 'urn:wisepipe-base-pipe:BoolToolVariable'
                },
                { type: 'boolean' }
            ]
        },
        alias: {
            oneOf: [
                {
                    $ref: 'urn:wisepipe-base-pipe:StringToolVariable'
                },
                { type: 'string', minLength: 1, maxLength: 150 }
            ]
        }
    },
    required: ['lhs', 'rhs', 'includeStartPeriod', 'alias']
};
let PeriodDiffConfig = PeriodDiffConfig_1 = class PeriodDiffConfig extends ToolConfig {
    constructor(lhs, rhs, includeStartPeriod, alias, customFormats) {
        super(ToolType.PERIOD_DIFF, customFormats);
        this.lhs = lhs;
        this.rhs = rhs;
        this.includeStartPeriod = includeStartPeriod;
        this.alias = alias;
        JSON.validate(this, PeriodDiffConfig_1);
    }
    // eslint-disable-next-line class-methods-use-this
    get in() {
        return PeriodDiffConfig_1.in;
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return PeriodDiffConfig_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return PeriodDiffConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return PeriodDiffConfig_1.outs;
    }
    static get in() {
        return PeriodDiffConfig_1.ins[0];
    }
    static get out() {
        return PeriodDiffConfig_1.outs[0];
    }
    static get ins() {
        return ['default'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.PERIOD_DIFF.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.CALCULATE], this.ins, this.outs, ['date', 'incentives', 'period', 'count', 'months', 'quarters', 'years', 'time span'], true);
    }
    applyParameters(parameters) {
        const baseVals = this.getBaseValues(parameters);
        return new PeriodDiffConfig_1(this.lhs.applyParameters(parameters), this.rhs.applyParameters(parameters), baseVals.includeStartPeriod, baseVals.alias, this.customFormats);
    }
    getBaseValues(parameters) {
        return {
            lhs: this.lhs.getBaseValues(parameters),
            rhs: this.rhs.getBaseValues(parameters),
            includeStartPeriod: parameters.getBoolVal(this.includeStartPeriod),
            alias: parameters.getStrVal(this.alias)
        };
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const source = ins.schemas.at(this.in);
        const exp = tsb.export(this.out);
        if (source) {
            const parameters = iConfigHelper.getParameters();
            const dependents = this.getDependents(exp, this.in, [
                ...Array.from(this.lhs.getColArgs(parameters)),
                ...Array.from(this.rhs.getColArgs(parameters))
            ]);
            exp.copyAll(this.in).makeNew(new Column(parameters.getStrVal(this.alias), 'number'), dependents, true);
        }
        exp.initializeColumnFormats().setColumnFormats(this.customFormats);
        return tsb.build();
    }
    isValidForCalc(iConfigHelper, ins) {
        const toolValidationResult = this.getToolValidationResult(iConfigHelper, ins);
        return toolValidationResult.validationState;
    }
    getToolValidationResult(iConfigHelper, scope) {
        const source = scope.schemas.at(this.in);
        const parameters = iConfigHelper.getParameters();
        const baseVals = this.getBaseValues(parameters);
        if (!source) {
            return ToolValidationResult.getMissingSourceColumnsResult();
        }
        // uncomment when we add diff comparison with period literals
        // if (
        // 	(this.lhs instanceof PeriodOperandLiteral && !baseVals.lhs.value) ||
        // 	(this.rhs instanceof PeriodOperandLiteral && !baseVals.rhs.value)
        // ) {
        // 	return ToolValidationResult.getBadMetadataResult('Period literal value is missing');
        // }
        // remove when we add diff comparison with period literals
        if (!(this.lhs instanceof PeriodOperandColumn && this.rhs instanceof PeriodOperandColumn)) {
            return ToolValidationResult.getInvalidResult('Only period columns are supported');
        }
        // if comparing two period columns, we need to ensure that the calendar level is the same
        if (this.lhs instanceof PeriodOperandColumn && this.rhs instanceof PeriodOperandColumn) {
            const lhsCol = source.find((c) => c.name === baseVals.lhs.name);
            const rhsCol = source.find((c) => c.name === baseVals.rhs.name);
            const lhsMetadata = lhsCol?.column.metadata;
            const rhsMetadata = rhsCol?.column.metadata;
            if (lhsMetadata !== undefined && rhsMetadata !== undefined) {
                if (lhsMetadata.calendarID !== rhsMetadata.calendarID) {
                    return ToolValidationResult.getInvalidResult('Calendar IDs must match');
                }
                if (lhsMetadata.level !== rhsMetadata.level) {
                    return ToolValidationResult.getInvalidResult('Calendar levels must match');
                }
            }
        }
        // uncomment when we add diff comparison with period literals
        // do we want to allow comparison of two period literals? or should we require at least one column
        const inputs = new Map();
        this.lhs.addInputReq(parameters, inputs);
        this.rhs.addInputReq(parameters, inputs);
        const sourceValidationResult = validateSourceWithDetail(source, inputs, [baseVals.alias]);
        return ToolValidationResult.getResultFromSourceValidation(sourceValidationResult);
    }
};
PeriodDiffConfig = PeriodDiffConfig_1 = __decorate([
    JsonSchemaV2(periodDiffConfigSchema, periodDiffConfigSchemaValidation),
    JsonDiscriminatorValue(ToolType.PERIOD_DIFF)
], PeriodDiffConfig);
export { PeriodDiffConfig };
//# sourceMappingURL=periodDiffConfig.js.map